import classNames from 'classnames';
import moment from 'moment-timezone';

import styles from '../styles.module.css';

const SHIFT_TAIL_MAX_FLEX_MINUTES = 60;
const SHIFT_TAIL_MAIN_ACTIVITY_GROW_RATIO = 0.25;

export const MAX_SHIFT_DENSITY_CHILD_COUNT = 12;

export const getActivityRootKey = (record) =>
	record?.rootKey ?? record?.key ?? `${record?.fromTimestamp}-${record?.toTimestamp}`;

const getActivityShiftId = (record, fallbackShiftId = null) =>
	record?.shiftId ?? record?.activity?.shiftId ?? record?.activity?.shift?.id ?? fallbackShiftId;

export const buildGroupedActiveItem = ({ childRecord, groupRecord, dayIndex, crewMemberId }) => ({
	...childRecord,
	id: childRecord?.id ?? childRecord?.key ?? null,
	rootKey: getActivityRootKey(childRecord),
	shiftId: getActivityShiftId(childRecord, groupRecord?.shift?.id ?? null),
	crewMemberId,
	dayIndex,
	key: childRecord?.key,
	shouldRenderClone: false,
});

export const shouldRenderActiveItemClone = (activeItem) =>
	Boolean(activeItem) && activeItem.shouldRenderClone !== false;

export const getActivityGroupChildKindClass = (record) => {
	if (record.isExtraActivity) return styles.activityGroupChildExtra;
	if (record.isShiftTail) return styles.activityGroupChildTail;
	return styles.activityGroupChildBase;
};

export const getActivityGroupChildClassNames = (record, activeItem) => {
	return classNames(getActivityGroupChildKindClass(record), {
		[styles.activityGroupChildHoverable]: !record.isShiftTail,
		[styles.activityGroupChildDimmed]:
			activeItem && activeItem.rootKey !== getActivityRootKey(record),
	});
};

const getDurationMinutes = (record) => {
	const start = moment(record.fromTimestamp);
	const end = moment(record.toTimestamp);
	const durationMinutes = end.diff(start, 'minutes');

	if (!start.isValid() || !end.isValid() || durationMinutes <= 0) return null;

	return durationMinutes;
};

const getShortestMainActivityDuration = (shiftChildren = []) => {
	const mainDurations = shiftChildren
		.filter((record) => !record.isExtraActivity && !record.isShiftTail)
		.map(getDurationMinutes)
		.filter(Boolean);

	return mainDurations.length ? Math.min(...mainDurations) : null;
};

export const getActivityGroupChildFlexStyle = (record, shiftChildren = []) => {
	if (record.isExtraActivity) return undefined;

	const durationMinutes = getDurationMinutes(record);

	if (!durationMinutes) return undefined;

	if (record.isShiftTail) {
		const shortestMainDuration = getShortestMainActivityDuration(shiftChildren);
		const tailMaxFlexMinutes = Math.min(
			SHIFT_TAIL_MAX_FLEX_MINUTES,
			shortestMainDuration
				? shortestMainDuration * SHIFT_TAIL_MAIN_ACTIVITY_GROW_RATIO
				: SHIFT_TAIL_MAX_FLEX_MINUTES
		);

		return { flexGrow: Math.min(durationMinutes, tailMaxFlexMinutes) };
	}

	return { flexGrow: durationMinutes };
};

export const getShiftDensityChildCount = (children = []) =>
	Math.min(children.length, MAX_SHIFT_DENSITY_CHILD_COUNT);
